import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import Logo from '../../../../assets/logo-two.png';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../../../store/store';
import { fetchProducts, clearPersonalDiscounts } from '../../../../store/ProductSlice';
import { PATHS } from '../../../../constants/routes';
import classes from './AdminLoginPage.module.css';

const AdminLoginPage: React.FC = () => {
    const [username, setUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [csrfToken, setCsrfToken] = useState<string>('');
    const [code, setCode] = useState<string>('');
    const [requires2FA, setRequires2FA] = useState<boolean>(false);
    const [twoFaChannel, setTwoFaChannel] = useState<'telegram' | 'email'>('telegram');
    const [resendCooldownMs, setResendCooldownMs] = useState<number>(0);
    const [resendTimer, setResendTimer] = useState<number>(0);
    const [telegramSentCount, setTelegramSentCount] = useState<number>(0);
    const [emailSentCount, setEmailSentCount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isTwoFaCodePending, setIsTwoFaCodePending] = useState<boolean>(false);

    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    // при монтировании компонента запрашиваем CSRF токен
    useEffect(() => {
        const fetchCsrfToken = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/csrf-token`, { withCredentials: true });
                setCsrfToken(response.data.csrfToken);
            } catch (err) {
                console.error('Ошибка получения CSRF токена', err);
            }
        };
        fetchCsrfToken();
    }, []);

    // таймер обратного отсчёта для повторной отправки кода
    useEffect(() => {
        if (resendCooldownMs <= 0) {
            setResendTimer(0);
            return;
        }
        setResendTimer(Math.ceil(resendCooldownMs / 1000));
        const started = Date.now();
        const interval = setInterval(() => {
            const passed = Date.now() - started;
            const left = resendCooldownMs - passed;
            if (left <= 0) {
                setResendTimer(0);
                setResendCooldownMs(0);
                clearInterval(interval);
            } else {
                setResendTimer(Math.ceil(left / 1000));
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [resendCooldownMs]);

    const handleLoginStep = async (channelOverride?: 'telegram' | 'email') => {
        setError('');
        setIsLoading(true);
        try {
            const payload: any = { username, password };
            if (channelOverride) {
                payload.channel = channelOverride;
            }
            const response = await axios.post(`${process.env.REACT_APP_SERVER_URL}/admin/login`, payload, {
                withCredentials: true,
                headers: {
                    'csrf-token': csrfToken,
                },
            });
            if (response.data.requires2FA) {
                setRequires2FA(true);
                setTwoFaChannel(response.data.channel || 'telegram');
                setResendCooldownMs(response.data.cooldownMs || 0);
                setTelegramSentCount(response.data.telegramSentCount || 0);
                setEmailSentCount(response.data.emailSentCount || 0);
                setIsTwoFaCodePending(response.data.channel === 'telegram');
            } else if (response.data.message) {
                // на будущее
                setIsTwoFaCodePending(false);
                dispatch(clearPersonalDiscounts());
                dispatch(fetchProducts());
                navigate('/admin');
            }
        } catch (err: any) {
            const responseData = err.response?.data;
            const isNetworkOrTimeoutError = !err.response || err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK';
            if (isNetworkOrTimeoutError) {
                setRequires2FA(false);
                setCode('');
                setResendCooldownMs(0);
                setTelegramSentCount(0);
                setEmailSentCount(0);
                setIsTwoFaCodePending(false);
                setError('Сервер не успел подтвердить отправку кода. Попробуйте ещё раз.');
                return;
            }
            const message = responseData?.message || 'Ошибка авторизации';
            const shouldKeep2FAState = responseData?.requires2FA === true;
            if (shouldKeep2FAState) {
                setRequires2FA(true);
                setTwoFaChannel(responseData.channel || 'telegram');
                setResendCooldownMs(responseData.cooldownMs || 0);
                setTelegramSentCount(responseData.telegramSentCount || 0);
                setEmailSentCount(responseData.emailSentCount || 0);
                setIsTwoFaCodePending((responseData.channel || 'telegram') === 'telegram');
                setError(message);
                return;
            }
            setRequires2FA(false);
            setCode('');
            setResendCooldownMs(0);
            setTelegramSentCount(0);
            setEmailSentCount(0);
            setIsTwoFaCodePending(false);
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!requires2FA) {
            await handleLoginStep();
            return;
        }

        // если уже на шаге ввода кода — отправляем /admin/verify
        try {
            setIsLoading(true);
            setError('');
            const response = await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/admin/verify`,
                { username, password, code },
                {
                    withCredentials: true,
                    headers: {
                        'csrf-token': csrfToken,
                    },
                },
            );
            if (response.data.message) {
                setIsTwoFaCodePending(false);
                dispatch(clearPersonalDiscounts());
                dispatch(fetchProducts());
                navigate('/admin');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Неверные учетные данные');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendTelegram = async () => {
        if (resendTimer > 0) return;
        if (telegramSentCount >= 3) {
            setError('Код уже был отправлен в Telegram, попробуйте запросить на e-mail');
            return;
        }
        await handleLoginStep('telegram');
    };

    const handleResendEmail = async () => {
        if (resendTimer > 0) return;
        if (emailSentCount >= 3) {
            setError('Лимит отправки кода на e-mail исчерпан, попробуйте позже');
            return;
        }
        await handleLoginStep('email');
    };

    return (
        <div className={classes.adminLogin}>
            <Link to={PATHS.main}>
                <img src={Logo} alt="Logo" className={classes.adminLogin__logo} />
            </Link>
            <div className={classes.adminLogin__content}>
                <h2 className={classes.adminLogin__content_title}>Вход</h2>
                {error && <p className={classes.adminLogin__content_error}>{error}</p>}
                <form className={classes.adminLogin__content_form} onSubmit={handleSubmit}>
                    <div className={classes.adminLogin__content_form_container}>
                        <label className={classes.adminLogin__content_form_label}>Логин:</label>
                        <input
                            className={classes.adminLogin__content_form_input}
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            disabled={isLoading}
                        />
                    </div>
                    <div className={classes.adminLogin__content_form_container}>
                        <label className={classes.adminLogin__content_form_label}>Пароль:</label>
                        <input
                            className={classes.adminLogin__content_form_input}
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {requires2FA && (
                        <>
                            <div className={classes.adminLogin__content_form_container}>
                                <label className={classes.adminLogin__content_form_label}>Код подтверждения:</label>
                                <input
                                    className={classes.adminLogin__content_form_input}
                                    type="text"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    required
                                    maxLength={6}
                                    disabled={isLoading}
                                />
                            </div>
                            <p className={classes.adminLogin__content_info}>
                                {isTwoFaCodePending && twoFaChannel === 'telegram'
                                    ? 'Код отправлен в Telegram. Из-за задержек доставки он может прийти не сразу.'
                                    : `Код был отправлен: ${twoFaChannel === 'telegram' ? 'в Telegram' : 'на e-mail'}.`}
                            </p>
                            {resendTimer > 0 ? (
                                <p className={classes.adminLogin__content_info}>Запросить код повторно можно через {resendTimer} сек.</p>
                            ) : (
                                <div className={classes.adminLogin__content_resendWrapper}>
                                    {telegramSentCount < 3 && (
                                        <button
                                            type="button"
                                            className={classes.adminLogin__content_form_button_secondary}
                                            onClick={handleResendTelegram}
                                            disabled={isLoading}
                                        >
                                            Повторить в Telegram
                                        </button>
                                    )}
                                    {emailSentCount < 3 && (
                                        <button
                                            type="button"
                                            className={classes.adminLogin__content_form_button_secondary}
                                            onClick={handleResendEmail}
                                            disabled={isLoading}
                                        >
                                            Отправить код на e-mail
                                        </button>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    <button className={classes.adminLogin__content_form_button} type="submit" disabled={isLoading}>
                        {requires2FA ? 'Подтвердить вход' : 'Войти'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default AdminLoginPage;
